import Image, { StaticImageData } from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { ClueCard } from '@snapi/luxury';
import clsx from 'clsx';
import YellowCirculeImg from '../what/images/yellow-circle.png';
import BottleImg from './images/bottle-illustration.png';
import SunImg from './images/sun-illustration.png';
import VegatablesImg from './images/vegetables.png';

interface ImproveSectionCardProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  icon: StaticImageData;
  className: string;
  color: string;
  clue?: JSX.Element;
}

function ImproveSectionCard({
  title,
  children,
  icon,
  className,
  color,
  clue,
}: ImproveSectionCardProps) {
  return (
    <div
      className={clsx(
        color,
        'relative flex flex-col pt-4 pl-5 pr-5 gap-y-2 w-[290px] h-[320px] rounded-2xl flex-none xl:w-[380px] xl:h-[280px]'
      )}
    >
      <div>{clue}</div>
      <div className={className}>
        <Image src={icon} alt="" />
      </div>
      <h4 className="text-body">{title}</h4>
      <p className="text-body">{children}</p>
    </div>
  );
}

const AllImproveCards: ImproveSectionCardProps[] = [
  {
    icon: VegatablesImg,
    title: 'Baby’s Nutrition',
    children:
      'Food comes first. Creating healthy habits when it comes to diet can have a huge impact on the development of a healthy gut microbiome.',
    className: 'w-[90px] h-[80px]',
    color: 'bg-pink-dim',
    clue: (
      <div className="absolute text-right w-[200px] font-mono text-body font-normal text-[24px] right-[16px]">
        <ClueCard
          description="Effect of Diet on the Gut Microbiota: Rethinking Intervention Duration"
          href="https://doi.org/10.3390/nu11122862"
          orientation="bottom"
        />
      </div>
    ),
  },
  {
    icon: BottleImg,
    title: 'Mothers Nutrition',
    children:
      'We know that not every mother can or wants to breastfeed, and that’s okay. If you’re breastfeeding, your diet can impact your baby’s gut microbiome.',
    className: 'w-[46px] h-[80px]',
    color: 'bg-pink-soft',
    clue: (
      <div className="absolute text-right w-[200px] font-mono text-body font-normal text-[24px] right-[16px]">
        <ClueCard
          description="Maternal breastmilk, infant gut microbiome and the impact on preterm infant health"
          href="https://doi.org/10.1111/apa.15534"
          orientation="bottom"
        />
      </div>
    ),
  },
  {
    icon: SunImg,
    title: 'Lifestyle Tips',
    children:
      'Environmental exposures can influence the development of the gut microbiome. Tips and tricks to help build a healthy environment can help get our baby’s microbiome where it needs to be!',
    className: 'w-[82px] h-[80px]',
    color: 'bg-blue-light',
    clue: (
      <div className="absolute text-right w-[200px] font-mono text-body font-normal text-[24px] right-[16px]">
        <ClueCard
          description="Gut microbiome development in early childhood is affected by day care attendance"
          href="https://doi.org/10.1038/s41522-021-00265-w"
          orientation="bottom"
        />
      </div>
    ),
  },
];

export function ImproveSectionScience() {
  return (
    <section
      id="evolution-section-science"
      className="overflow-x-hidden flex flex-col"
    >
      <div className="container relative">
        <div className="absolute top-[-110px] left-[-180px] w-[216px] sm:left-[-130px] sm:top-[-100px] lg:top-[-100px] lg:left-[-60px] xl:left-[60px]">
          <Image src={YellowCirculeImg} alt="" />
        </div>
        <div className="relative flex justify-center">
          <h2 className=" relative flex w-[278px] text-[28px] sm:w-[450px] md:text-[32px] md:w-[514px] text-center mb-3">
            How can I improve test results?
          </h2>
        </div>

        <p className="text-center mb-10 sm:w-[564px] mx-auto text-body">
          We know that not every baby is the same, and neither are our
          recommendations. We analyze your baby’s results to create personalized
          solutions that are easy, accessible, and proven to help. Our
          recommendations are determined by a logic that is based on what you
          share with us. These recommendations include:
        </p>
      </div>
      <div className="relative flex mb-[50px] w-full pl-3 sm:pl-10 lg:pl-0 lg:w-[930px] xl:w-[1200px] mx-auto">
        <Swiper
          slidesPerView="auto"
          spaceBetween={30}
          loop={true}
          pagination={{
            enabled: true,
            clickable: true,
            type: 'bullets',
            bulletElement: 'span',
            el: '#improve-pagination',
            bulletActiveClass: 'bg-body opacity-100',
          }}
          modules={[Pagination]}
        >
          <>
            {AllImproveCards.map((review, index) => (
              <SwiperSlide key={index} className="w-[290px] xl:w-[380px]">
                {(data) => (
                  <div className="flex flex-none ">
                    <ImproveSectionCard {...review} />
                  </div>
                )}
              </SwiperSlide>
            ))}
          </>
        </Swiper>
        <div
          id="improve-pagination"
          className="absolute flex flex-1 justify-center bottom-[-50px] inset-x-0 md:hidden"
        ></div>
      </div>
    </section>
  );
}
